import {
  decodeTelemetrySelection,
  disabledTelemetrySelection,
} from "../telemetry-selection-contract.js";
import {
  accountMutationHeaders,
  readBoundedAccountJson,
} from "./account-state-session-repository.js";
import { createAccountOperationId } from "./account-operation-id.js";

const SCHEMA_VERSION = 1;
const RESPONSE_MAX_BYTES = 12 * 1024;
const ACCOUNT_PATH = "/api/v1/account/privacy-controls";
const NOTICE_PATTERN = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/;

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactFields(value, fields) {
  if (!isRecord(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = fields.slice().sort();
  return actual.length === expected.length
    && actual.every((field, index) => field === expected[index]);
}

function positiveSafeInteger(value) {
  return Number.isSafeInteger(value) && value > 0;
}

export function validConsentNoticeVersion(value) {
  return typeof value === "string"
    && value.length > 0
    && value.length <= 128
    && NOTICE_PATTERN.test(value);
}

function freezeDecision(value) {
  return Object.freeze({ ...value });
}

function decodeTelemetryDecision(value) {
  if (
    hasExactFields(value, ["kind", "noticeVersion"])
    && ["granted", "denied"].includes(value.kind)
    && validConsentNoticeVersion(value.noticeVersion)
  ) {
    return freezeDecision(value);
  }
  throw new TypeError("Telemetry consent decision is invalid.");
}

function decodeDiscordDecision(value) {
  if (
    hasExactFields(value, ["kind", "noticeVersion"])
    && ["granted", "denied"].includes(value.kind)
    && validConsentNoticeVersion(value.noticeVersion)
  ) {
    return freezeDecision(value);
  }
  throw new TypeError("Discord-image consent decision is invalid.");
}

function decodeReceipt(value, decodeDecision, label) {
  if (
    !hasExactFields(value, ["decision", "decidedAtUnixMs"])
    || !positiveSafeInteger(value.decidedAtUnixMs)
  ) {
    throw new TypeError(`${label} receipt is invalid.`);
  }
  return Object.freeze({
    decision: decodeDecision(value.decision),
    decidedAtUnixMs: value.decidedAtUnixMs,
  });
}


function freezeProjection(value) {
  return Object.freeze({ ...value });
}

export function decodePrivacyControlsProjectionV1(value) {
  if (
    hasExactFields(value, ["kind", "schemaVersion"])
    && value.kind === "missing"
    && value.schemaVersion === SCHEMA_VERSION
  ) {
    return freezeProjection({ kind: "missing", schemaVersion: SCHEMA_VERSION });
  }
  if (
    !hasExactFields(value, [
      "kind",
      "schemaVersion",
      "revision",
      "telemetryConsent",
      "telemetrySelection",
      "telemetrySelectionApproved",
      "discordImageConsent",
    ])
    || value.kind !== "ready"
    || value.schemaVersion !== SCHEMA_VERSION
    || !positiveSafeInteger(value.revision)
    || (value.telemetryConsent !== null && !isRecord(value.telemetryConsent))
    || !isRecord(value.telemetrySelection)
    || typeof value.telemetrySelectionApproved !== "boolean"
    || (value.discordImageConsent !== null && !isRecord(value.discordImageConsent))
  ) {
    throw new TypeError("Privacy-controls projection is invalid.");
  }
  return freezeProjection({
    kind: "ready",
    schemaVersion: SCHEMA_VERSION,
    revision: value.revision,
    telemetryConsent: value.telemetryConsent === null
      ? null
      : decodeReceipt(value.telemetryConsent, decodeTelemetryDecision, "Telemetry consent"),
    telemetrySelection: decodeTelemetrySelection(value.telemetrySelection),
    telemetrySelectionApproved: value.telemetrySelectionApproved,
    discordImageConsent: value.discordImageConsent === null
      ? null
      : decodeReceipt(value.discordImageConsent, decodeDiscordDecision, "Discord-image consent"),
  });
}

function apiUrl(apiBaseUrl) {
  const base = new URL(String(apiBaseUrl ?? ""));
  if (
    !["http:", "https:"].includes(base.protocol)
    || base.username
    || base.password
    || base.pathname !== "/"
    || base.search
    || base.hash
  ) {
    throw new TypeError("Privacy-controls API base URL must be an HTTP origin.");
  }
  return `${base.origin}${ACCOUNT_PATH}`;
}

function precondition(projection) {
  return projection.kind === "missing"
    ? { kind: "missing" }
    : { kind: "revision", revision: projection.revision };
}

function commandFor(projection, mutation, operationId) {
  const common = {
    schemaVersion: SCHEMA_VERSION,
    operationId,
    precondition: precondition(projection),
  };
  switch (mutation.kind) {
    case "telemetryConsent":
      return { kind: "setTelemetryConsent", ...common, decision: mutation.decision };
    case "telemetrySelection":
      return { kind: "setTelemetrySelection", ...common, selection: mutation.selection };
    case "telemetrySelectionApproval":
      return { kind: "setTelemetrySelectionApproval", ...common, approved: mutation.approved };
    case "discordImageConsent":
      return { kind: "setDiscordImageConsent", ...common, decision: mutation.decision };
    default:
      throw new TypeError("Privacy-controls mutation is invalid.");
  }
}

function decodeAcknowledgement(value, operationId) {
  if (
    !hasExactFields(value, ["kind", "operationId", "authoritativeState"])
    || !["applied", "conflict"].includes(value.kind)
    || value.operationId !== operationId
  ) {
    throw new TypeError("Privacy-controls acknowledgement is invalid.");
  }
  return Object.freeze({
    kind: value.kind,
    operationId,
    authoritativeState: decodePrivacyControlsProjectionV1(value.authoritativeState),
  });
}

export function createPrivacyControlsAccountProvider({
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
  createOperationId = createAccountOperationId,
} = {}) {
  if (typeof fetchImpl !== "function" || typeof createOperationId !== "function") {
    throw new TypeError("Privacy-controls provider requires fetch and operation identity.");
  }
  const url = apiUrl(apiBaseUrl);

  async function load({ signal } = {}) {
    const response = await fetchImpl(url, {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      headers: { Accept: "application/json" },
      redirect: "error",
      signal,
    });
    if (response.status === 401) return freezeProjection({ kind: "signedOut" });
    if (!response.ok) throw new Error("Privacy-controls request failed.");
    return decodePrivacyControlsProjectionV1(
      await readBoundedAccountJson(response, "Account privacy controls", RESPONSE_MAX_BYTES),
    );
  }

  async function mutate(session, projection, mutation, { signal } = {}) {
    const operationId = createOperationId();
    const command = commandFor(projection, mutation, operationId);
    const response = await fetchImpl(url, {
      method: "POST",
      cache: "no-store",
      credentials: "include",
      headers: accountMutationHeaders(session),
      redirect: "error",
      signal,
      body: JSON.stringify(command),
    });
    if (response.status === 401) return freezeProjection({ kind: "signedOut" });
    if (![200, 409].includes(response.status)) {
      throw new Error("Privacy-controls mutation failed.");
    }
    const acknowledgement = decodeAcknowledgement(
      await readBoundedAccountJson(
        response,
        "Account privacy-controls acknowledgement",
        RESPONSE_MAX_BYTES,
      ),
      operationId,
    );
    if (
      (response.status === 200 && acknowledgement.kind !== "applied")
      || (response.status === 409 && acknowledgement.kind !== "conflict")
    ) {
      throw new TypeError("Privacy-controls status and acknowledgement disagree.");
    }
    return acknowledgement;
  }

  return Object.freeze({ load, mutate });
}

function sameState(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function signedOutProjection(value) {
  if (value?.kind !== "ready") return value;
  return {
    ...value,
    telemetryConsent: null,
    telemetrySelection: disabledTelemetrySelection(),
    telemetrySelectionApproved: false,
  };
}

export function createAccountAwarePrivacyControlsRepository({
  localRepository,
  sessionRepository,
  accountProvider = null,
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
  createOperationId,
} = {}) {
  const methods = (value, names) => names.every((name) => typeof value?.[name] === "function");
  if (
    !methods(localRepository, ["load", "mutate", "state", "subscribe"])
    || !methods(sessionRepository, ["load", "refresh", "state", "subscribe"])
  ) {
    throw new TypeError("Privacy controls require typed local and session repositories.");
  }
  const remote = accountProvider ?? createPrivacyControlsAccountProvider({
    apiBaseUrl,
    fetchImpl,
    ...(createOperationId === undefined ? {} : { createOperationId }),
  });
  let currentState = freezeProjection({ kind: "uninitialized" });
  let authority = "unknown";
  let accountId = null;
  let accountProjection = null;
  let requestRevision = 0;
  let controller = null;
  let sessionLoadDepth = 0;
  let disposed = false;
  const listeners = new Set();

  const state = () => currentState;
  const publish = (value) => {
    const next = freezeProjection(value);
    if (sameState(currentState, next)) return currentState;
    currentState = next;
    for (const listener of listeners) listener(currentState);
    return currentState;
  };
  const begin = ({ loading = false } = {}) => {
    requestRevision += 1;
    controller?.abort();
    controller = new AbortController();
    if (loading) publish({ kind: "loading" });
    return { revision: requestRevision, controller };
  };
  const isCurrent = (request) => !disposed
    && request.revision === requestRevision
    && !request.controller.signal.aborted;
  const activateLocal = () => {
    authority = "local";
    accountId = null;
    accountProjection = null;
    return publish(signedOutProjection(localRepository.load()));
  };
  const unavailable = (reason) => {
    authority = "unknown";
    accountId = null;
    accountProjection = null;
    return publish({ kind: "unavailable", operation: "account", reason });
  };
  const applyProjection = (projection, nextAccountId) => {
    authority = "account";
    accountId = nextAccountId;
    accountProjection = projection;
    return publish(projection);
  };
  const freshSession = async (request, refresh = false) => {
    sessionLoadDepth += 1;
    try {
      const session = await sessionRepository[refresh ? "refresh" : "load"]();
      return isCurrent(request) ? session : null;
    } finally {
      sessionLoadDepth -= 1;
    }
  };
  const readRemote = async (request, session, preserve = false) => {
    authority = "account";
    accountId = session.accountId;
    if (!preserve) {
      accountProjection = null;
      publish({ kind: "loading" });
    }
    try {
      const projection = await remote.load({ signal: request.controller.signal });
      if (!isCurrent(request)) return state();
      if (projection.kind === "signedOut") return activateLocal();
      return applyProjection(projection, session.accountId);
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return state();
      return unavailable("privacy-controls-request-failed");
    }
  };

  async function load() {
    if (disposed) return state();
    const priorAccountId = accountId;
    const preserve = authority === "account" && accountProjection !== null;
    const request = begin({ loading: !preserve });
    const session = await freshSession(request);
    if (!session || !isCurrent(request)) return state();
    if (session.kind === "signedOut") return activateLocal();
    if (session.kind !== "ready") return unavailable(session.reason || "session-unavailable");
    return readRemote(request, session, preserve && priorAccountId === session.accountId);
  }

  async function mutate(mutation) {
    if (authority === "local") return localRepository.mutate(mutation);
    if (authority !== "account" || !accountProjection) return state();
    const startingAccountId = accountId;
    const startingProjection = accountProjection;
    const request = begin();
    publish({ kind: "pending", operation: mutation.kind });
    try {
      const session = await freshSession(request, true);
      if (!session || !isCurrent(request)) return state();
      if (session.kind !== "ready" || session.accountId !== startingAccountId) {
        return unavailable("account-authority-changed");
      }
      const acknowledgement = await remote.mutate(
        session,
        startingProjection,
        mutation,
        { signal: request.controller.signal },
      );
      if (!isCurrent(request)) return state();
      if (acknowledgement.kind === "signedOut") return unavailable("account-authority-changed");
      if (acknowledgement.kind === "conflict") {
        accountProjection = acknowledgement.authoritativeState;
        return publish({
          kind: "conflict",
          operation: mutation.kind,
          authoritativeState: acknowledgement.authoritativeState,
        });
      }
      return applyProjection(acknowledgement.authoritativeState, session.accountId);
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return state();
      return unavailable("privacy-controls-mutation-failed");
    }
  }

  const subscribe = (listener) => {
    if (typeof listener !== "function") throw new TypeError("Privacy listener must be a function.");
    if (disposed) return () => {};
    listeners.add(listener);
    listener(currentState);
    return () => listeners.delete(listener);
  };
  const unsubscribeLocal = localRepository.subscribe((next) => {
    if (!disposed && authority === "local") publish(signedOutProjection(next));
  });
  const unsubscribeSession = sessionRepository.subscribe((session) => {
    if (disposed || sessionLoadDepth > 0) return;
    if (["loading", "uninitialized"].includes(session.kind)) {
      if (authority === "account" && accountProjection) return;
      authority = "unknown";
      accountId = null;
      accountProjection = null;
      begin({ loading: true });
    } else if (session.kind === "signedOut") {
      begin();
      activateLocal();
    } else if (session.kind === "ready") {
      const preserve = authority === "account"
        && accountId === session.accountId
        && accountProjection !== null;
      const request = begin({ loading: !preserve });
      void readRemote(request, session, preserve);
    } else {
      begin();
      unavailable(session.reason || "session-unavailable");
    }
  });

  function dispose() {
    disposed = true;
    requestRevision += 1;
    controller?.abort();
    listeners.clear();
    unsubscribeLocal();
    unsubscribeSession();
  }

  return Object.freeze({ dispose, load, mutate, state, subscribe });
}
